import { commonClient, type HotelListRecord } from '@bip/api-client';
import type { NearbyContext, NearbyFetchResult } from '../nearby.types';

function parseDateToYyyyMmDd(val: string | undefined): string {
  if (!val) return today();
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const cleaned = val.replace(/\s*-\s*\d{1,2}:\d{2}\s?(am|pm).*$/i, '').trim();
  const d = new Date(cleaned);
  if (!isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return today();
}

function nextDay(dateStr: string): string {
  const parsed = parseDateToYyyyMmDd(dateStr);
  const d = new Date(`${parsed}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function today(): string {
  return new Date().toISOString().split('T')[0];
}

function clampToToday(dateStr: string | undefined): string {
  const parsed = parseDateToYyyyMmDd(dateStr);
  return parsed < today() ? today() : parsed;
}

async function resolveNationality(): Promise<string> {
  try {
    const cached = (localStorage.getItem('checkoutCountry') || '').toUpperCase();
    if (cached) return cached;
  } catch {
    // localStorage disabled — fall through to geo lookup
  }
  try {
    const res = await fetch('https://ipapi.co/json/');
    const data = await res.json();
    const code = (data?.country_code || '').toUpperCase();
    if (code) {
      try {
        localStorage.setItem('checkoutCountry', code);
      } catch {
        // quota / disabled — proceed without persistence
      }
      return code;
    }
  } catch {
    // ipapi unreachable
  }
  return '';
}

/**
 * Nearby-hotels fetcher. Goes through the common-service cross-sell hub
 * (`GET /bip-api/common/nearby/hotels`), which fans out to
 * `hotels-service /search/nearby-hotels` server-side. Response shape is
 * `HotelListRecord[]` inside `data.Records` — same as before the hub
 * refactor, so the card component reads it unchanged.
 */
export async function fetchNearbyHotels(ctx: NearbyContext): Promise<NearbyFetchResult<HotelListRecord>> {
  const checkin = clampToToday(ctx.checkin ?? today());
  const checkout = nextDay(checkin);
  const nationality = ctx.nationality || (await resolveNationality());

  // Only the hotels-service `/search/nearby-hotels` allowlist —
  // `rooms` / `children` / `city` / `countryCode` are rejected by the
  // endpoint's `forbidNonWhitelisted` validation. `countryCode` is
  // still resolved (unused here; kept in case a future consumer needs
  // it), and `nationality` is passed when we have it — same rule the
  // hotels widget uses everywhere else.
  //
  // `refLabel` = the consuming page's own venue/restaurant name
  // (`ctx.referenceName`) — threading it makes hotels-service:
  //   (a) add a "Distance from {refLabel}" filter section on the
  //       search-results page the Explore link lands on, and
  //   (b) bake `refLat`/`refLon`/`refLabel`/`showFilter` into the
  //       emitted `searchResultsPageUrl` so all four ride through.
  // Without it, the Explore link opens a plain city search with no
  // venue-relative distance filter — the reported gap on restaurants
  // + concerts detail pages.
  // void countryCode;
  const res = await commonClient.getNearbyHotels({
    latitude: ctx.latitude,
    longitude: ctx.longitude,
    checkin,
    checkout,
    adults: '2',
    // Fetch limit is 5 (count = 5) per requirements.
    count: String(5),
    ...(ctx.userCurrency ? { userCurrency: ctx.userCurrency } : {}),
    ...(nationality ? { nationality } : {}),
    ...(ctx.referenceName ? { refLabel: ctx.referenceName } : {}),
  });

  const records = res?.records ?? [];

  return {
    items: records,
    total: res?.totalCount ?? records.length,
    exploreHref: res?.searchResultsPageUrl || '/hotels',
  };
}
